/**
 * The hub's `budgets` reads — the administration view of the money: who in an organization may
 * spend how much, and how much is left.
 *
 * Two calls, not one, and the split is deliberate. The screen is master–detail: the tree shows
 * every team, but only ONE team's roster at a time, and a person costs a spend read each. So the
 * org call is bounded by the number of TEAMS, and the per-person fan-out is paid only for the team
 * the owner actually opened.
 *
 * Wire (actions on the principal itself, per the hub's `{type}/{id}/{action}` grammar — which is
 * also what puts each call on the right authorization target, since the entity in the path IS the
 * one the admin check asks about):
 *   `GET /api/v1/graph/organization/<id>/budgets`
 *   `GET /api/v1/graph/team/<id>/budgets`
 *
 * These are READS. A scope with no pool answers `endpoint_id: null` rather than creating one —
 * setting a budget up is `tokenPlanService.setupOrg(orgId)` / `setupTeam(teamId)`, a separate decision.
 * Writing a budget is a plain `LLMEndpoint` entity update (`limits.cost_usd_total`); handing one
 * out is `llmEndpointsService.allocate()`. No budgets action does any of that.
 */
import { dataManager } from '../APIEntity';
import { hubAction } from './llm-endpoints-service';
import type { TokenPlanSetupResult } from './token-plan-service';

/** A budget below a switched root whose own model list names models the new provider does not offer. */
export interface NeedsModelChange {
  endpoint_id: string;
  name: string;
  /** The team (or org) that owns the budget, when it has one. */
  scope_name: string | null;
  models: string[];
}

export interface SwitchPayingProviderResult {
  endpoint_id: string;
  /** False when the org was already on this provider and base URL (nothing changed). */
  switched: boolean;
  provider: string;
  needs_model_change: NeedsModelChange[];
}

/** One principal's pool, as the budgets screen reads it. */
export interface ScopeBudget {
  /** The principal's id (organization or team) — not the endpoint's. */
  id: string;
  name: string;
  /** The scope's pool endpoint (a typeid); null when it has no budget yet. */
  endpoint_id: string | null;
  /** The pool's lifetime cap (`limits.cost_usd_total`); null = uncapped. */
  limit_usd: number | null;
  spent_usd: number;
  /** Lifetime tokens billed against this pool, off the same report as `spent_usd` — the two
   *  numbers can never disagree with each other. */
  spent_tokens: number;
  /**
   * Sum of the CHILDREN's caps, and `null` where the call did not read them — the org view totals
   * its teams, the team view totals its people, and a team row inside the ORG view leaves it null
   * rather than fanning out over every person for a number that screen does not show.
   *
   * It may legitimately exceed `limit_usd`: a pool is allowed to promise more than it holds, and
   * the excess is caught when the money is spent, not when it is promised. The PAGE nonetheless
   * refuses to create such an overspend at the point of typing — see `available-to-allocate.ts`.
   */
  allocated_usd: number | null;
  /**
   * May the caller change what this pool itself holds — its total, its name, its per-window
   * ceilings? Answered by the hub, as the same `update` policy question the resulting request will
   * be judged by, so a control the page offers always works and one it hides would always have
   * been refused. Never re-derive it here from a role string: an org admin's standing on a budget
   * row is DERIVED from the scope it hangs under, and only the hub can resolve that.
   */
  can_configure: boolean;
  /**
   * May the caller hand a share of this pool downward (`allocate`)? What "Add people" needs on a
   * team's pool. False on an organization's own pool, and that is not a gap: people are funded
   * from their team's pool, never straight off the org's.
   */
  can_allocate: boolean;
  /**
   * May the caller rename or delete this team/organization — the `update` question on the SCOPE
   * ENTITY, not on its pool. Different entity, different answer: a shared org's admin renames its
   * teams freely and the organization itself not at all.
   */
  can_manage: boolean;
  /** May the caller add a team here? Adding one is a scoped `create` on the organization. */
  can_add_child: boolean;
  /** The caller's own top role on this TEAM — `null` when they are not on its roster. Set only on
   *  a team's row in the org read; absent from an older hub. */
  my_roles?: string[];
  /** How many people hold this team directly. Set only on a team's row in the org read. */
  member_count?: number | null;
}

/**
 * The organization's own row — the one place a pool may hold its OWN provider credential instead
 * of drawing on another endpoint. A team's and a person's pool are always chains, so these three
 * fields exist only here rather than on the shared `ScopeBudget` every row uses: on a chain they
 * would forever read `false` / `null` / `''`, sent and never looked at.
 */
export interface OrgScopeBudget extends ScopeBudget {
  is_root: boolean;
  /** `'openrouter' | 'anthropic' | 'openai'` when `is_root`; `null` otherwise. */
  provider: string | null;
  /** `****last4` when a root has a stored key, `''` otherwise. Never the key itself. */
  credential_hint: string;
  /** May the caller set or replace the provider key the organization pays with? Owner-only today. */
  can_set_credential: boolean;
  /**
   * May the caller give this organization a budget at all? Separate from `can_set_credential`,
   * which is asked of the POOL and therefore answers `false` for everyone — the owner included —
   * while the organization still has none. This one is asked of the organization.
   */
  can_set_up_budget: boolean;
  /**
   * May the caller share this organization — invite people, re-role them, remove them? Admin and
   * above, and so deliberately WIDER than everything else on this row: running the people is what
   * an organization is shared for.
   */
  can_invite: boolean;
}

/** One person's allowance under a team pool. */
export interface MemberBudget {
  /** The person's own endpoint (a typeid) — the thing whose cap is edited. */
  endpoint_id: string;
  name: string;
  email: string | null;
  user_id: string | null;
  limit_usd: number | null;
  spent_usd: number;
  spent_tokens: number;
  /** May the caller change this person's allowance? The `update` question, asked of one allowance. */
  can_configure: boolean;
  /**
   * May the caller remove THIS person from the team? Per row, because the request it gates is
   * judged per row: the hub applies the role ladder (a member at or above your own rank is
   * untouchable) and the last-owner rule, neither of which is a fact about the team. Gating the
   * button on the scope-wide `ScopeBudget.can_manage` ("may I rename the team") offered an admin a
   * Remove beside a peer admin and earned a 403 on press.
   *
   * False on a row nobody holds yet — there is no membership to revoke.
   */
  can_remove: boolean;
}

/**
 * Someone who belongs to the ORGANIZATION itself and to none of its teams.
 *
 * Visibility, not money: the page lists people through their team, so anyone holding the org
 * directly — an admin invited to the organization, an owner who never joined a class — appeared
 * nowhere on it. These rows carry who they are and what they hold on the org, and stop there;
 * their spend shows on the pool they draw from, where it already does.
 */
export interface OrgPerson {
  user_id: string;
  name: string;
  email: string | null;
  /** Their highest role on the organization — `owner` / `admin` / `member` / … */
  roles: string[];
}

export interface OrgBudgets {
  org: OrgScopeBudget;
  teams: ScopeBudget[];
  /** The org's own people — those in none of its teams. Empty in an org run entirely through teams. */
  people: OrgPerson[];
}

export interface TeamBudgets {
  team: ScopeBudget;
  members: MemberBudget[];
}

export class BudgetsService {
  /** The organization's pool and one row per team in it. Admin on the org. */
  orgBudgets(orgId: string): Promise<OrgBudgets> {
    return dataManager.callAction<undefined, OrgBudgets>(hubAction('budgets', 'organization', orgId, 'GET'));
  }

  /** One team's pool and one row per person drawing on it. Admin on the team. */
  teamBudgets(teamId: string): Promise<TeamBudgets> {
    return dataManager.callAction<undefined, TeamBudgets>(hubAction('budgets', 'team', teamId, 'GET'));
  }

  /**
   * Make the organization the paying entity on its OWN provider key, instead of an allowance
   * drawn from Flowpad's shared global root. Creates the org's default as a ROOT with no sources;
   * the key itself is set afterward through `llmEndpointsService.setCredential` on the returned
   * `endpoint_id` — this call never carries one. Idempotent on an existing root; refuses (409,
   * surfaced as a thrown error) if the org already draws from a shared pool, since converting a
   * chain into a root in place is not offered.
   */
  setPayingProvider(orgId: string, body: { provider: string; base_url?: string }): Promise<TokenPlanSetupResult> {
    const info = hubAction('set-paying-provider', 'organization', orgId, 'POST');
    info.bodyParameters = { provider: body.provider, base_url: body.base_url ?? '' };
    return dataManager.callAction<undefined, TokenPlanSetupResult>(info);
  }

  /**
   * Move the organization's own root to ANOTHER provider, in place: the root keeps its id, so every team
   * and person below it stays. The stored key is cleared (it belonged to the old provider); set the new
   * one on `endpoint_id` afterwards. The answer names every budget below whose own model list the new
   * provider does not serve -- those need a model chosen.
   */
  switchPayingProvider(
    orgId: string,
    body: { provider: string; base_url?: string },
  ): Promise<SwitchPayingProviderResult> {
    const info = hubAction('switch-paying-provider', 'organization', orgId, 'POST');
    info.bodyParameters = { provider: body.provider, base_url: body.base_url ?? '' };
    return dataManager.callAction<undefined, SwitchPayingProviderResult>(info);
  }
}

export const budgetsService = new BudgetsService();
